import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useUserPreferences } from './useUserPreferences';

/**
 * Per-screen display preferences.
 *
 * Each route keeps its own set, because the answer to "how dense should this table be" is
 * different for an audit log you skim and an asset register you read. They are saved to the user's
 * account (see `useUserPreferences`), so they follow the person across browsers and devices.
 *
 * Keyed by the route's path with its ids stripped, so `/assets/abc` and `/assets/def` share one
 * set of preferences rather than accumulating one per record ever opened.
 */

export interface PagePrefs {
  density: 'comfortable' | 'compact';
  /** Rows a table requests per page. */
  pageSize: number;
  /** Column ids the reader has switched off on this screen. */
  hiddenColumns: string[];
}

export const DEFAULT_PAGE_PREFS: PagePrefs = {
  density: 'comfortable',
  pageSize: 25,
  hiddenColumns: [],
};

export const PAGE_SIZES = [10, 25, 50, 100] as const;

type PrefsByPage = Record<string, Partial<PagePrefs>>;

/**
 * `/assets/9f3c-…` and `/assets/1a2b-…` are the same screen.
 *
 * Anything that looks like an id - a uuid, a number, or any long opaque segment - collapses to
 * `:id`, so a preference set on one record's detail page applies to every record of that kind
 * rather than accumulating one entry per record ever opened.
 */
export function pageKeyFor(pathname: string): string {
  return (
    pathname
      .split('/')
      .map((segment) =>
        /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(segment) ||
        /^[0-9]+$/.test(segment) ||
        segment.length > 20
          ? ':id'
          : segment,
      )
      .join('/') || '/'
  );
}

interface PagePrefsValue {
  prefs: PagePrefs;
  setPrefs: (patch: Partial<PagePrefs>) => void;
  reset: () => void;
  /** The screen these preferences belong to. */
  pageKey: string;
}

const PagePrefsContext = createContext<PagePrefsValue | null>(null);

export function PagePrefsProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const pageKey = pageKeyFor(pathname);
  // Saved on the user's account, under one section of their preferences document.
  const { document, setSection } = useUserPreferences();
  const store = (document.pages as PrefsByPage | undefined) ?? {};
  // The user's own defaults, set in Settings, sit between the built-in defaults and a page's own
  // choices: a page someone has customised keeps its customisation, every other page follows them.
  const userDefaults = document.pageDefaults as Partial<PagePrefs> | undefined;

  const pageEntry = store[pageKey];
  const prefs = useMemo<PagePrefs>(
    () => ({ ...DEFAULT_PAGE_PREFS, ...userDefaults, ...pageEntry }),
    [userDefaults, pageEntry],
  );

  const setPrefs = useCallback(
    (patch: Partial<PagePrefs>) => {
      setSection('pages', { ...store, [pageKey]: { ...store[pageKey], ...patch } });
    },
    [setSection, store, pageKey],
  );

  const reset = useCallback(() => {
    const next = { ...store };
    delete next[pageKey];
    setSection('pages', next);
  }, [setSection, store, pageKey]);

  const value = useMemo(
    () => ({ prefs, setPrefs, reset, pageKey }),
    [prefs, setPrefs, reset, pageKey],
  );

  return <PagePrefsContext.Provider value={value}>{children}</PagePrefsContext.Provider>;
}

/**
 * Preferences for the current screen.
 *
 * Returns the defaults outside a provider rather than throwing, so a component can be rendered in
 * a test or in isolation without having to stand the provider up first.
 */
export function usePagePrefs(): PagePrefsValue {
  const context = useContext(PagePrefsContext);
  return (
    context ?? {
      prefs: DEFAULT_PAGE_PREFS,
      setPrefs: () => {},
      reset: () => {},
      pageKey: '/',
    }
  );
}

/**
 * The columns a table may show, registered by whichever table is on screen.
 *
 * The settings menu lives in the page header and the table lives in the body, so the two need a
 * channel between them. The table publishes what it has; the menu renders the toggles. Without
 * this the only alternative is every page threading its column list up into its own header, which
 * is the per-page wiring this design exists to avoid.
 */
interface TableRegistration {
  columns: { id: string; label: string }[];
}

const TableRegistryContext = createContext<{
  registration: TableRegistration | null;
  register: (registration: TableRegistration | null) => void;
} | null>(null);

export function TableRegistryProvider({ children }: { children: ReactNode }) {
  const [registration, setRegistration] = useState<TableRegistration | null>(null);
  const value = useMemo(
    () => ({ registration, register: setRegistration }),
    [registration],
  );
  return (
    <TableRegistryContext.Provider value={value}>{children}</TableRegistryContext.Provider>
  );
}

export function useTableRegistry() {
  return useContext(TableRegistryContext);
}
